// src/components/DashboardOverview.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { overviewQueries, goalQueries, sentimentQueries, staffQueries } from '../repository/graphQueries'; 
import { formatDate, formatScore } from '../utils';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';
import { DRI_EXPLANATION } from '../constants';

const COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="relative inline-block text-left">
            <div>
                <button type="button" className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" onClick={() => setIsOpen(!isOpen)}>
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
            </div>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input type="checkbox" className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" checked={selectedOptions.includes(option)} onChange={() => onChange(option)} />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    return (
        <div className="relative inline-block">
            <div className="inline-flex items-center cursor-help" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onClick={() => setIsVisible(!isVisible)}>
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full right-0 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full right-2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const cleanDriList = (rawDriList) => {
    if (!rawDriList) return [];
    const separators = / and | & | \/ | \\ | with | for |,| featuring | assisting | cc /i;
    const allNames = rawDriList.flatMap(dri => {
        if (typeof dri !== 'string') return [];
        const cleaned = dri.replace(/\(.*\)|\[.*\]/g, '');
        return cleaned.split(separators);
    });
    const finalDris = allNames
        .map(name => name.trim())
        .filter(name => name && name.length > 1);
    return [...new Set(finalDris)].sort();
};

const DashboardOverview = () => {
    const { neo4jService, clientId } = useOutletContext();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [priorityCounts, setPriorityCounts] = useState({ 
        criticalIssues: 0, blockedInitiatives: 0, overdueActions: 0, neglectedGoals: 0,
        totalGoals: 0, totalIssues: 0, totalInitiatives: 0,
    });
    const [goalsAtRisk, setGoalsAtRisk] = useState([]);
    const [criticalIssues, setCriticalIssues] = useState([]);
    const [sentimentData, setSentimentData] = useState([]);
    const [commFrequencyData, setCommFrequencyData] = useState([]);
    const [commTypes, setCommTypes] = useState([]);
    
    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(false);
    const [selectedDris, setSelectedDris] = useState([]);
    const driTooltipContent = DRI_EXPLANATION;

    const loadDris = useCallback(async () => {
        if (!neo4jService || !clientId) return;
        setLoadingDris(true);
        try {
            const result = await neo4jService.runQuery(staffQueries.getDistinctDRIsForClient, { clientId });
            const rawDriList = result.map(r => r.dri);
            setDris(cleanDriList(rawDriList));
        } catch (err) {
            console.error('Error loading DRIs for client:', err);
        } finally {
            setLoadingDris(false);
        }
    }, [neo4jService, clientId]);

    const loadDashboardData = useCallback(async () => {
        if (!neo4jService || !clientId) return;
        setLoading(true); setError(null);

        try {
            const queryParams = { 
                clientId, 
                driFilter: selectedDris.length > 0 ? selectedDris : null 
            };
            
            const results = await Promise.all([
                overviewQueries.getPriorityCriticalIssuesCount,
                overviewQueries.getPriorityBlockedInitiativesCount,
                overviewQueries.getPriorityOverdueActionsCount,
                overviewQueries.getPriorityNeglectedGoalsCount,
                goalQueries.getGoalsAtRisk,
                overviewQueries.getIssuesByRiskScore,
                sentimentQueries.getSentimentOverTime,
                overviewQueries.getCommunicationFrequencyByType,
                goalQueries.getActiveGoalsCount,
                overviewQueries.getPriorityTotalIssuesCount,
                overviewQueries.getPriorityTotalInitiativesCount,
            ].map(query => neo4jService.runQuery(query, queryParams)));

            setPriorityCounts({
                criticalIssues: results[0]?.[0]?.count ?? 0,
                blockedInitiatives: results[1]?.[0]?.count ?? 0,
                overdueActions: results[2]?.[0]?.count ?? 0,
                neglectedGoals: results[3]?.[0]?.count ?? 0,
                totalGoals: results[8]?.[0]?.count ?? 0,
                totalIssues: results[9]?.[0]?.count ?? 0,
                totalInitiatives: results[10]?.[0]?.count ?? 0,
            });
            setGoalsAtRisk(results[4] ?? []);
            setCriticalIssues(results[5] ?? []);

            const rawSentimentResults = results[6];
            const processedSentimentData = Array.isArray(rawSentimentResults)
                ? rawSentimentResults.map(item => ({
                    day: formatDate(item.weekBeginning),
                    avgSentiment: typeof item.avgSentiment === 'number' ? item.avgSentiment : parseFloat(item.avgSentiment)
                })).filter(item => item.day && typeof item.avgSentiment === 'number' && !isNaN(item.avgSentiment))
                : [];
            setSentimentData(processedSentimentData);

            const rawCommFreq = results[7];
            if (Array.isArray(rawCommFreq)) {
                const uniqueTypes = [...new Set(rawCommFreq.map(item => item.commType))];
                setCommTypes(uniqueTypes);
                const pivotedData = rawCommFreq.reduce((acc, item) => {
                    const week = formatDate(item.weekBeginning);
                    if (!week) return acc;
                    if (!acc[week]) {
                        acc[week] = { weekBeginning: week };
                        uniqueTypes.forEach(type => { acc[week][type] = 0; });
                    }
                    acc[week][item.commType] = item.weeklyCount;
                    return acc;
                }, {});
                const chartData = Object.values(pivotedData).sort((a, b) => new Date(a.weekBeginning) - new Date(b.weekBeginning));
                setCommFrequencyData(chartData);
            } else {
                setCommFrequencyData([]);
                setCommTypes([]);
            }

        } catch (err) {
            console.error('Error loading dashboard overview data:', err);
            setError(`Overview Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [neo4jService, clientId, selectedDris]);

    useEffect(() => {
        if (clientId) loadDris();
    }, [clientId, loadDris]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev => prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]);
    };

    const handleGoalRiskClick = (goal) => {
        if (goal?.goalId) navigate(`/clients/${clientId}/goals/${goal.goalId}`);
        else navigate(`/clients/${clientId}/goals`);
    };

    const handleCriticalIssueClick = (issue) => {
        if (issue?.issueId) navigate(`/clients/${clientId}/issues/${issue.issueId}`);
        else navigate(`/clients/${clientId}/issues`);
    };

    if (loading) return <div className="text-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mx-auto mb-2"></div>Loading Overview...</div>;
    if (error) return <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">Error loading overview: {error}</div>;

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl font-semibold">Overview</h2>
                 <div className="flex items-center space-x-2">
                    <DropdownCheckbox
                        options={dris}
                        selectedOptions={selectedDris}
                        onChange={handleDriChange}
                        title="Filter by DRI"
                    />
                    <Tooltip content={driTooltipContent}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-500 cursor-help" />
                    </Tooltip>
                 </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-red-100 border border-red-200 rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-red-700 mb-1">High Sev. Issues</h3>
                    <p className="text-3xl font-bold text-red-800">{priorityCounts.criticalIssues} <span className="text-lg text-red-600">/ {priorityCounts.totalIssues}</span></p>
                    <p className="text-xs text-red-600 mt-1">{clientId}</p>
                </div>
                <div className="bg-orange-100 border border-orange-200 rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-orange-700 mb-1">Blocked Initiatives</h3>
                    <p className="text-3xl font-bold text-orange-800">{priorityCounts.blockedInitiatives} <span className="text-lg text-orange-600">/ {priorityCounts.totalInitiatives}</span></p>
                    <p className="text-xs text-orange-600 mt-1">{clientId}</p>
                </div>
                <div className="bg-yellow-100 border border-yellow-200 rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-yellow-700 mb-1">Overdue Actions</h3>
                    <p className="text-3xl font-bold text-yellow-800">{priorityCounts.overdueActions}</p>
                    <p className="text-xs text-yellow-600 mt-1">{clientId}</p>
                </div>
                <div className="bg-purple-100 border border-purple-200 rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-purple-700 mb-1">Active Goals</h3>
                    <p className="text-3xl font-bold text-purple-800">{priorityCounts.totalGoals}</p>
                    <p className="text-xs text-purple-600 mt-1">{clientId}</p>
                </div>
            </div>
                    
            <div className="mt-6">
                 <EisenhowerMatrixView
                    clientId={clientId}
                    typeFilter={['Goal', 'Initiative', 'ActionItem', 'Issue']}
                    driFilter={selectedDris}
                />
            </div>

             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-6 md:mb-8">
                <div className="bg-white rounded-lg shadow p-4 md:p-6">
                     <h3 className="text-lg font-medium text-gray-900 mb-4">Top Priority Goals</h3>
                     <div className="overflow-x-auto">
                         {goalsAtRisk.length > 0 ? (
                             <table className="min-w-full divide-y divide-gray-200 text-sm">
                                 <thead className="bg-gray-50">
                                     <tr>
                                         <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Goal</th>
                                         <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Priority Score</th>
                                     </tr>
                                 </thead>
                                 <tbody className="bg-white divide-y divide-gray-200">
                                     {goalsAtRisk.map((goal, index) => (
                                         <tr key={goal.goalId || index} onClick={() => handleGoalRiskClick(goal)} className='hover:bg-gray-50 cursor-pointer'>
                                             <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">{goal.goalName}</td>
                                             <td className="px-3 py-2 whitespace-nowrap">
                                                 {formatScore(goal.score)}
                                             </td>
                                         </tr>
                                     ))}
                                 </tbody>
                             </table>
                         ) : (
                           <div className="text-center text-sm text-gray-500 py-4">No active goals match the current filter.</div>
                         )}
                     </div>
                 </div>

                <div className="bg-white rounded-lg shadow p-4 md:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Critical Issues by Risk</h3>
                    <div className="overflow-x-auto">
                         {criticalIssues.length > 0 ? (
                             <table className="min-w-full divide-y divide-gray-200 text-sm">
                                 <thead className="bg-gray-50">
                                     <tr>
                                         <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Issue</th>
                                         <th scope="col" className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Risk Score</th>
                                     </tr>
                                 </thead>
                                 <tbody className="bg-white divide-y divide-gray-200">
                                     {criticalIssues.map((item, index) => (
                                         <tr key={item.issueId || index} onClick={() => handleCriticalIssueClick(item)} className='hover:bg-gray-50 cursor-pointer'>
                                             <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">{item.issueName}</td>
                                             <td className="px-3 py-2 whitespace-nowrap">
                                                 {formatScore(item.riskScore)}
                                             </td>
                                         </tr>
                                     ))}
                                 </tbody>
                             </table>
                         ) : (
                             <div className="text-center text-sm text-gray-500 py-4">No critical issues match the current filter.</div>
                         )}
                     </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 md:p-6 mt-6 md:mt-8">
                 <h3 className="text-lg font-medium text-gray-900 mb-4">Communication Frequency (Weekly)</h3>
                  {commFrequencyData.length > 0 && commTypes.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                         <LineChart data={commFrequencyData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                           <CartesianGrid strokeDasharray="3 3" />
                           <XAxis dataKey="weekBeginning" tick={{fontSize: 10}} />
                           <YAxis allowDecimals={false} tick={{fontSize: 10}} />
                           <RechartsTooltip wrapperClassName="text-xs" />
                           <Legend />
                           {commTypes.map((type, index) => (
                               <Line
                                   key={type}
                                   type="monotone"
                                   dataKey={type}
                                   stroke={COLORS[index % COLORS.length]}
                                   strokeWidth={2}
                                   dot={{r: 3}}
                                   activeDot={{r: 5}}
                                   name={type}
                                   connectNulls={true}
                               />
                           ))}
                         </LineChart>
                       </ResponsiveContainer>
                  ) : (
                      <div className="text-center text-sm text-gray-500 py-4">
                          No communication data available for this period.
                      </div>
                  )}
             </div>

            <div className="bg-white rounded-lg shadow p-4 md:p-6 mt-6 md:mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Sentiment Trend (Weekly)</h3>
                 {Array.isArray(sentimentData) && sentimentData.length > 0 ? (
                     <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={sentimentData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="day" tick={{fontSize: 10}} />
                          <YAxis tick={{fontSize: 10}} />
                          <RechartsTooltip
                              wrapperClassName="text-xs"
                              formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Avg Sentiment']}
                              labelFormatter={(value) => `Week Starting: ${value}`}
                          />
                          <Line
                              type="monotone"
                              dataKey="avgSentiment"
                              stroke="#3B82F6"
                              strokeWidth={2}
                              dot={{r: 3}}
                              activeDot={{r: 5}}
                              name="Avg Sentiment"
                              connectNulls={true}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                 ) : (
                     <div className="text-center text-sm text-gray-500 py-4">
                         {loading ? 'Loading sentiment data...' : 'No sentiment data available for this period.'}
                     </div>
                 )}
            </div>
        </>
    );
};

export default DashboardOverview;